import { FC } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import { useAuthContext } from '@/context/AuthContext.tsx';
import useOrgCredits from '@/api/useOrgCredits.ts';
import Routes from '@/router/Routes.ts';

type CreditPackage = {
  name: string;
  credits: number;
  price: string;
  linkEnvKey: keyof ImportMetaEnv;
};

const PACKAGES: CreditPackage[] = [
  { name: 'Starter', credits: 5, price: '$49', linkEnvKey: 'VITE_STRIPE_LINK_STARTER' },
  { name: 'Standard', credits: 15, price: '$129', linkEnvKey: 'VITE_STRIPE_LINK_STANDARD' },
  { name: 'Executive', credits: 40, price: '$299', linkEnvKey: 'VITE_STRIPE_LINK_EXECUTIVE' },
];

const CreditsPage: FC = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuthContext();
  const { creditsRemaining, isLoading: isCreditsLoading } = useOrgCredits();
  const [searchParams] = useSearchParams();
  const purchaseSuccess = searchParams.get('success') === 'true';

  const orgId = userProfile?.org_id;

  const buildUrl = (envKey: keyof ImportMetaEnv): string => {
    const base = import.meta.env[envKey] as string | undefined;
    if (!base || !orgId) return '#';
    return `${base}?client_reference_id=${orgId}`;
  };

  return (
    <section className="flex flex-col items-center gap-8 px-4 py-12">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="font-dm text-2xl font-bold text-[#0F1B2A]">{t('creditsPage.title')}</h1>
        <p className="font-dm text-sm text-black/55">{t('creditsPage.description')}</p>
        {!isCreditsLoading && (
          <p className="font-dm text-sm font-semibold text-[#0F1B2A]">
            {t('homePage.creditsRemaining', { count: creditsRemaining })}
          </p>
        )}
      </div>

      {purchaseSuccess && (
        <div className="flex flex-col items-center gap-3 w-full max-w-sm">
          <Alert status="success">{t('creditsPage.successMessage')}</Alert>
          <Link
            to={Routes.DASHBOARD}
            className="w-full py-3 px-4 rounded-xl font-dm font-bold text-sm text-center bg-[#0F1B2A] text-white hover:opacity-90 transition-opacity"
          >
            Return to Executive Surface
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-3xl">
        {PACKAGES.map((pkg) => (
          <a
            key={pkg.name}
            href={buildUrl(pkg.linkEnvKey)}
            className="flex flex-col gap-4 rounded-2xl border border-black/10 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col gap-1">
              <span className="font-dm text-xs font-semibold uppercase tracking-widest text-black/40">
                {pkg.name}
              </span>
              <span className="font-dm text-3xl font-bold text-[#0F1B2A]">{pkg.price}</span>
            </div>
            <p className="font-dm text-sm text-black/70">
              {t('creditsPage.creditsCount', { count: pkg.credits })}
            </p>
            <span className="mt-auto inline-block rounded-xl bg-[#0F1B2A] px-4 py-2 text-center font-dm text-sm font-bold text-white">
              {t('creditsPage.buyButton')}
            </span>
          </a>
        ))}
      </div>
    </section>
  );
};

export default CreditsPage;
